// État affiché d'une carte de collecteur ACTIF — logique PURE.
// Extraite de legacy.js le 2026-08-13, en corrigeant le défaut qui l'a motivée.
//
// ── LE DÉFAUT ───────────────────────────────────────────────────────────────
// L'action `collecteurs_view` ne renvoyait en « actifs » que les collecteurs
// EDI ou DÉJÀ pourvus d'un secret. Un collecteur `creds` jamais configuré
// n'apparaissait donc NULLE PART : filtré côté actifs, et absent de l'annuaire
// des inactifs, qui exclut tout slug déjà référencé comme collecteur.
// Sept des onze collecteurs actifs étaient dans ce trou — cerp, ocp, sy, upsa,
// hub, digipharmacie, mail_etik. Blocage circulaire : impossible de saisir des
// identifiants pour un collecteur qu'on ne voit pas, impossible de le voir tant
// qu'on n'a pas saisi d'identifiants.
//
// ── CE QUE LA CORRECTION A RÉVÉLÉ ───────────────────────────────────────────
// En les faisant apparaître, l'ancien barème est devenu FAUX : il annonçait
// « Configuré » à tout collecteur dont le connecteur n'était pas prêt, sans
// jamais regarder s'il avait des identifiants. SY se serait affiché
// « Configuré · Bientôt » avec zéro identifiant enregistré.
//
// D'où l'ordre des tests ci-dessous : l'absence d'identifiant se dit AVANT
// l'état du connecteur, parce que c'est la seule des deux sur laquelle le
// titulaire peut agir. « Bientôt » reste en macaron quand les deux sont vrais.
import { fdate } from "./format.js";

/** Modes où le titulaire n'a aucun identifiant à saisir : le flux arrive seul. */
export function sansIdentifiants(mode) {
  return mode === "edi" || mode === "mail";
}

/**
 * Classe de carte + badge + macaron éventuel.
 * `enTest` : un test de connexion est en cours pour ce collecteur (état client).
 */
export function statutCollecteur(c, enTest = false) {
  if (c.mode === "edi") return { card: "st-edi", cls: "edi", txt: "EDI automatique" };
  if (c.mode === "mail") return { card: "st-edi", cls: "edi", txt: "Boîte mail" };
  if (enTest) return { card: "st-wait", cls: "wait", txt: "Test en cours…" };
  if (!c.has_secret) {
    return { card: "st-none", cls: "none", txt: "À configurer",
             macaron: c.connecteur_pret ? undefined : "Bientôt" };
  }
  if (!c.connecteur_pret) return { card: "st-wait", cls: "wait", txt: "Configuré", macaron: "Bientôt" };
  switch (c.statut) {
    case "ok":           return { card: "st-ok",   cls: "ok",   txt: "Connecté" };
    case "erreur_auth":  return { card: "st-err",  cls: "err",  txt: "Erreur d'auth" };
    case "jamais_teste": return { card: "st-wait", cls: "wait", txt: "Configuré · non testé" };
    case "desactive":    return { card: "st-none", cls: "off",  txt: "Désactivé" };
    default:             return { card: "st-none", cls: "none", txt: "Configuré" };
  }
}

/** Ligne d'explication sous le nom : ce qu'il y a à faire, ou ce qui se passe. */
export function sousTitreCollecteur(c) {
  if (sansIdentifiants(c.mode)) return "Flux automatique — aucun identifiant requis.";
  const compte = c.login ? "Compte " + c.login + " · " : "";
  if (!c.has_secret) {
    return compte + (c.connecteur_pret
      ? "aucun identifiant enregistré — cliquez sur « Configurer »."
      : "aucun identifiant enregistré — la collecte démarrera dès que le connecteur sera prêt.");
  }
  if (!c.connecteur_pret) return compte + "connexion bientôt disponible — vous serez notifié.";
  if (c.statut === "ok") return compte + "dernier accès OK " + (c.dernier_ok_at ? fdate(c.dernier_ok_at) : "—");
  if (c.statut === "erreur_auth") return "⚠ " + (c.derniere_erreur || "identifiants refusés au dernier essai");
  if (c.login) return "Compte " + c.login;
  return "";
}

/** Libellé du bouton d'action, ou null quand le mode n'en demande aucune. */
export function actionCollecteur(c) {
  if (sansIdentifiants(c.mode)) return null;
  return c.has_secret ? "Remplacer" : "Configurer";
}

/**
 * Sépare les sources siphonnables selon que leur connecteur est prêt ou non.
 *
 * `pretParSlug` : Map slug → `connecteur_pret`, bâtie depuis `collecteurs_view`.
 * Elle peut manquer — les deux appels de `loadCollecteurs` sont indépendants et
 * celui du catalogue peut échouer seul. Alors on ne SAIT pas : `sait:false`,
 * tout reste dans `pretes`, et l'appelant rend la liste à plat. Un instrument
 * qui ne sait pas se tait ; replier trente sources sur une map vide afficherait
 * un faux avec l'aplomb d'un vrai.
 *
 * Un slug absent de la map reste lui aussi dans `pretes` : seul un
 * `connecteur_pret` explicitement faux replie un bouton. Le défaut corrigé ici
 * est « le bouton est introuvable » — le replier à tort le reproduirait.
 */
export function separerSources(sources, pretParSlug) {
  const liste = sources || [];
  if (!pretParSlug || !pretParSlug.size) return { pretes: liste, bientot: [], sait: false };
  const pretes = [];
  const bientot = [];
  for (const s of liste) (pretParSlug.get(s) === false ? bientot : pretes).push(s);
  return { pretes, bientot, sait: true };
}
